import type { SessionKeyData } from '@polaris/core/models'
import {
  type INamadaSigner,
  NamadaMpcSigner,
  NoopWarnNamadaSigner,
} from '@polaris/core/namada'
import { vaultSessionDataAtom } from '@polaris/core/stores/mpc/atom'
import { useAtomValue } from 'jotai'
import { useMemo } from 'react'
import { useLitClient } from '@/src/hooks/mpc/use-lit-client'
import { mpcNamadaKeysAtom } from '@/src/stores/mpc/atoms'
import { useNamadaChain } from './use-namada-chain'

/**
 * Hook to get Namada MPC wallet signer
 * Encapsulates the logic for creating a NamadaMpcSigner from locally stored MPC session data.
 * Pre-warms the signer cache with derived keys from the atom if available.
 */
export const useNamadaWalletSigner = (): INamadaSigner => {
  const vaultSessionData = useAtomValue(vaultSessionDataAtom)
  const chainInfo = useNamadaChain()
  const { litClient } = useLitClient()
  const { data: namadaKeys } = useAtomValue(mpcNamadaKeysAtom)

  // Construct MPC session key data from vault session data
  const mpcSessionKeyData: SessionKeyData | undefined = useMemo(() => {
    if (!vaultSessionData?.authSig || !vaultSessionData?.sessionKeyPair) {
      return undefined
    }
    return {
      walletSig: vaultSessionData.authSig,
      sessionKeyPair: vaultSessionData.sessionKeyPair,
    }
  }, [vaultSessionData])

  // Create the signer instance with pre-warmed cache
  const signer = useMemo(() => {
    if (!mpcSessionKeyData) {
      return new NoopWarnNamadaSigner(
        'Namada wallet signer requires MPC session key data',
      )
    }

    if (!chainInfo) {
      return new NoopWarnNamadaSigner(
        'Namada wallet signer requires chain info',
      )
    }

    return new NamadaMpcSigner(
      litClient,
      mpcSessionKeyData,
      chainInfo,
      namadaKeys,
    )
  }, [mpcSessionKeyData, chainInfo, namadaKeys, litClient])

  return signer
}
